import { ReactElement, useMemo, useState } from "react";
import { Button, Col, Form, notification, Row, Space } from "antd";

import { IRecommendationSettings } from "types/recommendations.types";
import { getFormErrorFields } from "utils/errors";
import { getUTCDate } from "utils/date";
import { ValidityFields } from "../ValidityFields";
import { DisplaySettingsFields } from "../DisplaySettingsFields";

type RecommendationsFormProps<T> = {
  children?: ReactElement | ReactElement[];
  initialValues?: Partial<T>;
  onSubmit: (values: T) => Promise<any>;
  btnText?: string;
  citiesDisabled?: boolean;
};

export const RecommendationsForm = <T extends IRecommendationSettings>({
  children,
  initialValues = {},
  onSubmit,
  btnText = "Далее",
  citiesDisabled,
}: RecommendationsFormProps<T>) => {
  const [form] = Form.useForm<T>();
  const [loading, setLoading] = useState(false);

  const handleFinish = async (values: T) => {
    setLoading(true);
    try {
      const { age, ...rest } = values;
      if (!age?.from && !age?.to) {
        const data:any = { ...rest, age: null };
        if (!data.cityIds) {
          data.cityIds = [];
        }
        await onSubmit(data as any);
      } else {
        const data:any = { ...values };
        if (!data.cityIds) {
          data.cityIds = [];
        }
        await onSubmit(data);
      }
    } catch (e: any) {
      if (e && e.violations) {
        const errorFields = getFormErrorFields(e.violations, {});
        form.setFields(errorFields);
      } else {
        notification.error(e);
      }
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = () => {
    form.submit();
  };

  const formatInitialFields = useMemo(() => {
    const { workTimeInterval, ...rest } = initialValues;
    return {
      ...rest,
      workTimeInterval: {
        from: getUTCDate(workTimeInterval?.from),
        to: getUTCDate(workTimeInterval?.to),
      },
    };
  }, [initialValues]);

  return (
    <Form
      initialValues={formatInitialFields}
      preserve={false}
      scrollToFirstError={true}
      form={form}
      labelWrap
      autoComplete="off"
      onFinish={handleFinish}
      layout="vertical"
      fields={[
        {
          name: "workTimeInterval",
          touched: true,
        },
        {
          name: "age",
          touched: true,
        },
      ]}
    >
      {children}
      <ValidityFields form={form} />
      <DisplaySettingsFields citiesDisabled={citiesDisabled} form={form} />
      <Row justify="end">
        <Space>
          <Col>
            <Button loading={loading} onClick={handleSubmit}>
              {btnText}
            </Button>
          </Col>
        </Space>
      </Row>
    </Form>
  );
};
